import React, { useCallback, useEffect, useState } from "react";
import styles from "./Languages.module.css";
import { catchError, getUserDataFromJwt } from "@_utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import { settlementActions } from "@_actions/settlement_portal.actions";
import { languageActions } from "@_actions/language.actions";
import SettlementPortalTexts from "@_components/SettlementPortalTexts/SettlementPortalTexts";
import TranslationRow from "@_components/TranslationRow/TranslationRow";
import UpgradePlanpopup from "@_components/popups/UpgradePlanPopup/UpgradePlanPopup.js";

import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import subscriptionCopy from "@_configs/subscription.config.js";
import UpgradeImage from "@_assets/images/add_translation.png";
import Scrollbar from "react-perfect-scrollbar-z";
import { sidebarActions } from "@_actions/sidebar.actions";
import { sidebarConstants } from "@_constants/sidebar.constants";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import LanguageList from "@_configs/LanguageList.config";

function Languages() {
  const dispatch = useDispatch();
  const anchorRef = React.useRef(null);
  const jwtData = getUserDataFromJwt();
  const languageReducer = useSelector((state) => state.languageReducer);
  const [openLanguagePopup, setOpenLanguagePopup] = useState(false);
  const [displayUpgrade, setDisplayUpgrade] = useState(false);
  const subscriptionReducer = useSelector((state) => state.subscriptionReducer);
  const location = useLocation();
  const history = useHistory();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const { snackbarProps, showSnackbarSuccess, showSnackbarError } =
    useSnackbar();

  const { title, subtitle, copy_1, upgradeButton } =
    subscriptionCopy.translation[jwtData.subscription.toLowerCase()];

  const handleOpenLanguagePopup = (e) => {
    if (
      jwtData.subscription.toLowerCase() !== "enterprise" &&
      languageReducer.translations &&
      languageReducer.translations.translations.length >=
        subscriptionReducer.subscriptionConfig.max_translations
    ) {
      setDisplayUpgrade(true);
    } else {
      history.push("/admin/settlement_portal/translations");
      dispatch(languageActions.selectTranslation(null));
      setOpenLanguagePopup((prev) => !prev);
    }
  };

  const handleCloseLanguagePopup = () => {
    setOpenLanguagePopup(false);
  };

  const getErrorMessage = useCallback((message) => {
    if (
      message ===
      "Invalid request: translation has to be complete before activation."
    ) {
      return "All required fields have to be present before activating a translation.";
    }

    if (message === "Missing required property: content") {
      return "This field cannot be empty when your translation is active!";
    }

    if (
      message === "Invalid request: cannot deactivate assigned translations."
    ) {
      return "You cannot deactivate a translation that is assigned to a case!";
    }

    if (
      message === "Invalid request: cannot deactivate default translations."
    ) {
      return "You cannot deactivate your default translation!";
    }

    if (
      message === "Invalid request: cannot delete an activated translation."
    ) {
      return "You cannot delete an activated translation.";
    }

    return message;
  }, []);

  useEffect(() => {
    dispatch(settlementActions.getSettlement());
    dispatch(languageActions.getLanguages());
    dispatch(languageActions.getTranslations());
    dispatch(sidebarActions.updateStatus(sidebarConstants.TRANSLATIONS));
  }, [dispatch]);

  useEffect(() => {
    if (languageReducer.status.TRANSLATION_GET === "success") {
      if (
        pathParts.length === 4 &&
        pathParts[2] === "translations" &&
        pathParts[3]
      ) {
        const langInPathISO = LanguageList.find(
          (lang) => lang.language.toLowerCase() === pathParts[3].toLowerCase(),
        )?.code;
        if (!langInPathISO) {
          history.push("/page_not_found");
        } else {
          const pathTranslations =
            languageReducer.translations.translations.find(
              (translation) => translation.language === langInPathISO,
            );
          if (pathTranslations) {
            dispatch(languageActions.selectTranslation(pathTranslations));
          } else {
            history.push("/page_not_found");
          }
        }
      }
    } else if (catchError(languageReducer.status.TRANSLATIONS_GET)) {
      const rawMessage =
        languageReducer.status.LANGUAGES_GET ||
        languageReducer.status.TRANSLATIONS_GET;
      showSnackbarError(getErrorMessage(rawMessage));

      dispatch(languageActions.clearStatusMessage("LANGUAGES_GET"));
      dispatch(languageActions.clearStatusMessage("TRANSLATIONS_GET"));
    } else return;
    // eslint-disable-next-line
  }, [dispatch, getErrorMessage, languageReducer.status, showSnackbarError]);

  const handleSelectLanguage = (language) => {
    dispatch(languageActions.addTranslation({ language }));
    setOpenLanguagePopup(false);
    dispatch(languageActions.selectTranslation({ language: language }));
  };

  // display POST success & error
  useEffect(() => {
    if (languageReducer.status.TRANSLATION_POST === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("TRANSLATION_POST"));
      }, 4000);
    } else if (catchError(languageReducer.status.TRANSLATION_POST)) {
      showSnackbarError(
        getErrorMessage(languageReducer.status.TRANSLATION_POST),
      );
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("TRANSLATION_POST"));
      }, 4000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // display DELETE success & error
  useEffect(() => {
    if (languageReducer.status.TRANSLATION_DELETE === "success") {
      showSnackbarSuccess("Success!");

      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("TRANSLATION_DELETE"));
      }, 4000);
    } else if (catchError(languageReducer.status.TRANSLATION_DELETE)) {
      showSnackbarError(
        getErrorMessage(languageReducer.status.TRANSLATION_DELETE),
      );
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("TRANSLATION_DELETE"));
      }, 4000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // display PUT success & error
  // login text
  useEffect(() => {
    if (languageReducer.status.LOGIN_TEXT_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("LOGIN_TEXT_PUT"));
      }, 4000);
    } else if (catchError(languageReducer.status.LOGIN_TEXT_PUT)) {
      showSnackbarError(getErrorMessage(languageReducer.status.LOGIN_TEXT_PUT));
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("LOGIN_TEXT_PUT"));
      }, 7000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // case overview text
  useEffect(() => {
    if (languageReducer.status.CASE_OVERVIEW_TEXT_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("CASE_OVERVIEW_TEXT_PUT"));
      }, 4000);
    } else if (catchError(languageReducer.status.CASE_OVERVIEW_TEXT_PUT)) {
      showSnackbarError(
        getErrorMessage(languageReducer.status.CASE_OVERVIEW_TEXT_PUT),
      );
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("CASE_OVERVIEW_TEXT_PUT"));
      }, 7000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // about us text
  useEffect(() => {
    if (languageReducer.status.ABOUT_US_TEXT_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("ABOUT_US_TEXT_PUT"));
      }, 4000);
    } else if (catchError(languageReducer.status.ABOUT_US_TEXT_PUT)) {
      showSnackbarError(
        getErrorMessage(languageReducer.status.ABOUT_US_TEXT_PUT),
      );
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("ABOUT_US_TEXT_PUT"));
      }, 7000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // faq
  useEffect(() => {
    if (languageReducer.status.FAQ_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("FAQ_PUT"));
      }, 4000);
    } else if (catchError(languageReducer.status.FAQ_PUT)) {
      showSnackbarError(getErrorMessage(languageReducer.status.FAQ_PUT));
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("FAQ_PUT"));
      }, 7000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // default translation
  useEffect(() => {
    if (languageReducer.status.DEFAULT_TRANSLATION_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("DEFAULT_TRANSLATION_PUT"));
      }, 4000);
    } else if (catchError(languageReducer.status.DEFAULT_TRANSLATION_PUT)) {
      showSnackbarError(
        getErrorMessage(languageReducer.status.DEFAULT_TRANSLATION_PUT),
      );
      setTimeout(() => {
        dispatch(languageActions.clearStatusMessage("DEFAULT_TRANSLATION_PUT"));
      }, 7000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // activate
  useEffect(() => {
    if (languageReducer.status.ACTIVATE_TRANSLATION_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(
          languageActions.clearStatusMessage("ACTIVATE_TRANSLATION_PUT"),
        );
      }, 4000);
    } else if (catchError(languageReducer.status.ACTIVATE_TRANSLATION_PUT)) {
      showSnackbarError(
        getErrorMessage(languageReducer.status.ACTIVATE_TRANSLATION_PUT),
      );
      setTimeout(() => {
        dispatch(
          languageActions.clearStatusMessage("ACTIVATE_TRANSLATION_PUT"),
        );
      }, 7000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // deactivate
  useEffect(() => {
    if (languageReducer.status.DEACTIVATE_TRANSLATION_PUT === "success") {
      showSnackbarSuccess("Success!");
      setTimeout(() => {
        dispatch(
          languageActions.clearStatusMessage("DEACTIVATE_TRANSLATION_PUT"),
        );
      }, 4000);
    } else if (catchError(languageReducer.status.DEACTIVATE_TRANSLATION_PUT)) {
      showSnackbarError(
        getErrorMessage(languageReducer.status.DEACTIVATE_TRANSLATION_PUT),
      );
      setTimeout(() => {
        dispatch(
          languageActions.clearStatusMessage("DEACTIVATE_TRANSLATION_PUT"),
        );
      }, 7000);
    } else return;
  }, [
    dispatch,
    getErrorMessage,
    languageReducer.status,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  useEffect(() => {
    if (languageReducer.status.TRANSLATION_POST === "success") {
      dispatch(languageActions.getTranslations());
    }
  }, [languageReducer.status.TRANSLATION_POST, dispatch]);

  return (
    <div className={styles.root}>
      <AppSnackbar {...snackbarProps} />

      <div className={styles.languages_container}>
        <div className={styles.language_list_container}>
          {" "}
          <div className={styles.add_language_container}>
            <div className={styles.edit_icon_container}>
              <Button
                className={styles.btn_add_language}
                onClick={handleOpenLanguagePopup}
                ref={anchorRef}
                aria-controls={
                  openLanguagePopup ? "add-language-menu" : undefined
                }
                aria-haspopup="true"
              >
                <b className={styles.plus_sign}>+</b>Add Translation
              </Button>
              <Menu
                id="add-language-menu"
                anchorEl={anchorRef.current}
                open={openLanguagePopup}
                onClose={handleCloseLanguagePopup}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                {languageReducer.languages &&
                  languageReducer.languages.map(
                    (language) =>
                      languageReducer.translations?.translations &&
                      !languageReducer.translations.translations.some(
                        (trans) => trans.language === language,
                      ) && (
                        <MenuItem
                          onClick={() => handleSelectLanguage(language)}
                          key={language}
                        >
                          <b> {language.toUpperCase()}</b>
                        </MenuItem>
                      ),
                  )}
              </Menu>
            </div>
          </div>
          <Scrollbar
            options={{ suppressScrollX: true }}
            maxHeight="calc(100vh - 180px)"
            height="calc(100vh - 180px)"
          >
            {languageReducer.translations?.translations &&
              languageReducer.translations.translations.map((translation) => (
                <TranslationRow
                  translation={translation}
                  key={translation.id}
                />
              ))}
          </Scrollbar>
        </div>

        <div className={styles.tabs_and_content_container}>
          {languageReducer.selected_translation ? (
            <SettlementPortalTexts />
          ) : (
            <div className={styles.translation_no_data}>
              NO TRANSLATION IS SELECTED!
            </div>
          )}
        </div>
      </div>
      {displayUpgrade && (
        <UpgradePlanpopup
          title={title}
          subtitle={subtitle}
          imageUrl={UpgradeImage}
          copy_1={copy_1}
          upgradeButton={upgradeButton}
          open={displayUpgrade}
          close={() => setDisplayUpgrade(false)}
        />
      )}
    </div>
  );
}

export default Languages;
